import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getRecordingPermissionsAsync,
  requestRecordingPermissionsAsync,
  setAudioModeAsync,
  useAudioRecorder,
  RecordingPresets,
} from 'expo-audio';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { deleteAsync } from 'expo-file-system/legacy';
import { useExerciseSound } from './useExerciseSound';
import { logError } from '@/utils/logHandling';
import { Alert, Linking, Platform } from 'react-native';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';

export function useExerciseRecord(
  changer: unknown,
  timeoutSeconds: number,
  uniqueId: string,
) {
  const [isRecording, setIsRecording] = useState(false);
  const recorder = useAudioRecorder(RecordingPresets.HIGH_QUALITY);

  const timeout = useRef<any>(null);
  const [uri, setUri] = useState('');
  const allowPlay = useRef(true);

  const [microphonePermission, setMicrophonePermission] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    (async () => {
      const { granted } = await getRecordingPermissionsAsync();
      setMicrophonePermission(granted);
    })();
  }, []);

  const { playSound, isPlaying, stopSound } = useExerciseSound(uri);
  const { showNotification } = useNotification();

  const showNoPermissionToast = () => {
    if (Platform.OS === 'web') {
      showNotification({
        type: 'error',
        title: 'Kein Zugriff auf das Mikrofon erteilt.',
      });
    } else {
      Alert.alert(
        'Kein Mikrofonzugriff',
        'Erlauben Sie den Zugriff auf das Mikrofon in den Einstellungen.',
        [
          {
            text: 'Einstellungen',
            onPress: Linking.openSettings,
          },
          {
            text: 'Abbrechen',
          },
        ],
      );
    }
  };

  const startRecording = useCallback(async () => {
    try {
      allowPlay.current = true;
      if (microphonePermission === null || !microphonePermission) {
        const { granted } = await requestRecordingPermissionsAsync();
        setMicrophonePermission(granted);
        if (!granted) {
          showNoPermissionToast();
          return;
        }
      }

      // Prevent multiple simultaneous recordings
      if (isRecording || recorder.isRecording) {
        logError('Recording is already in progress');
        return;
      }

      await setAudioModeAsync({
        allowsRecording: true,
        playsInSilentMode: true,
      });

      // Add timeout to prevent hanging while the recorder prepares
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Recording creation timeout')), 8000),
      );
      // Pass options so iOS regenerates the recording file (new UUID URL) on
      // every take instead of reusing/overwriting the same path. Web/Android
      // already mint a fresh uri; this makes iOS behave the same.
      await Promise.race([
        recorder.prepareToRecordAsync(RecordingPresets.HIGH_QUALITY),
        timeoutPromise,
      ]);
      recorder.record();
      setIsRecording(true);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      if (
        errorMessage.includes('permission') ||
        errorMessage.includes('Permission')
      ) {
        setMicrophonePermission(false);
        showNoPermissionToast();
      } else {
        logError('Failed to start recording', error);
      }
    }
  }, [microphonePermission, recorder]);

  const stopRecording = useCallback(
    async (notAbort = true) => {
      clearTimeout(timeout.current);
      setIsRecording(false);

      try {
        if (recorder.isRecording) {
          await recorder.stop();
        }
      } catch (error) {
        logError('Failed to stop recorder', error);
      }
      // Recording is over: switch the shared audio session back to playback.
      // A lingering record session on iOS routes/attenuates playback, which
      // makes the correct-answer audio (played through expo-video) start
      // silently while its button already shows the playing state.
      try {
        await setAudioModeAsync({
          allowsRecording: false,
          playsInSilentMode: true,
        });
      } catch (error) {
        logError('Failed to reset audio mode after recording', error);
      }
      if (!notAbort) {
        return;
      }
      const uri = recorder.uri;
      if (!uri) {
        throw new Error('No audio recording uri');
      }
      setUri(uri);

      // Cache the recording if uniqueId exists
      if (uniqueId) {
        try {
          const previousUri = await AsyncStorage.getItem(
            `@recording:${uniqueId}`,
          );
          // Replace the old take: delete the previously stored file so takes
          // don't accumulate. Skipped on web, where the uri is a blob URL and
          // not a deletable file-system path.
          if (Platform.OS !== 'web' && previousUri && previousUri !== uri) {
            await deleteAsync(previousUri, { idempotent: true });
          }
          await AsyncStorage.setItem(`@recording:${uniqueId}`, uri);
        } catch (error) {
          logError('Failed to cache recording', error);
        }
      }
    },
    [uniqueId, recorder],
  );

  const playRecording = () => {
    playSound();
  };

  const stopPlayingRecording = () => {
    stopSound();
  };

  useEffect(() => {
    if (!isRecording && uri && allowPlay.current) {
      playSound();
    }
  }, [uri]);

  useEffect(() => {
    stopSound();
    if (uri) {
      setUri('');
    }
    if (isRecording) {
      stopRecording(false);
    }
  }, [changer]);

  useEffect(() => {
    if (isRecording) {
      timeout.current = setTimeout(() => {
        stopRecording(true);
      }, Number(timeoutSeconds));
    }
    return () => {
      clearTimeout(timeout.current);
    };
  }, [isRecording]);

  useEffect(() => {
    const getCachedRecording = async () => {
      if (uniqueId) {
        try {
          const cachedRecording = await AsyncStorage.getItem(
            `@recording:${uniqueId}`,
          );
          if (cachedRecording) {
            allowPlay.current = false;
            setUri(cachedRecording);
          }
        } catch (error) {
          logError('Failed to retrieve cached recording', error);
        }
      }
    };

    getCachedRecording();
  }, [uniqueId]);

  useEffect(() => {
    return () => {
      clearTimeout(timeout.current);
      // On native, expo-audio releases the recorder on unmount, which ends
      // the capture (calling stop() here would race that teardown and throw
      // NativeSharedObjectNotFoundException). On web there is no such
      // teardown, so moving to the next exercise remounts this hook while the
      // MediaRecorder/mic stream keeps running — stop it explicitly.
      if (Platform.OS !== 'web') {
        return;
      }
      try {
        if (recorder.isRecording) {
          recorder.stop();
        }
      } catch (error) {
        logError('Failed to stop recorder on web cleanup', error);
      }
    };
  }, [recorder]);

  return {
    stopRecording,
    startRecording,
    isRecording,
    playRecording,
    stopPlayingRecording,
    isPlaying,
    uri,
  };
}
